// =============================================================================
// consoleCapture.js - Console Log Capture Utility
// Save to: src/utils/consoleCapture.js
// =============================================================================

class ConsoleCapture {
  constructor(maxLogs = 50) {
    this.logs = [];
    this.maxLogs = maxLogs;
    this.isStarted = false;
    this.originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info
    };
  }

  start() {
    if (this.isStarted) return;
    this.isStarted = true;

    // Capture log, warn, and error (skip info which is rarely used)
    ['log', 'warn', 'error'].forEach(method => {
      console[method] = (...args) => {
        this.logs.push({
          type: method,
          timestamp: new Date().toISOString(),
          message: this.formatArgs(args)
        });

        if (this.logs.length > this.maxLogs) {
          this.logs.shift();
        }

        this.originalConsole[method].apply(console, args);
      };
    });

    window.addEventListener('error', (event) => {
      this.logs.push({
        type: 'uncaught-error',
        timestamp: new Date().toISOString(),
        message: `${event.message} at ${event.filename}:${event.lineno}:${event.colno}`
      });
      if (this.logs.length > this.maxLogs) {
        this.logs.shift();
      }
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.logs.push({
        type: 'unhandled-rejection',
        timestamp: new Date().toISOString(),
        message: this.formatArgs([event.reason])
      });
      if (this.logs.length > this.maxLogs) {
        this.logs.shift();
      }
    });

    console.log('🔍 Console capture initialized');
  }

  formatArgs(args) {
    return args.map(arg => {
      let str;
      if (arg instanceof Error) {
        str = `${arg.name}: ${arg.message}${arg.stack ? '\n' + arg.stack : ''}`;
      } else if (typeof arg === 'object') {
        try {
          str = JSON.stringify(arg);
        } catch {
          str = String(arg);
        }
      } else {
        str = String(arg);
      }
      return str.length > 500 ? str.substring(0, 500) + '...' : str;
    }).join(' ');
  }

  getLogs() {
    return [...this.logs];
  }

  getLogsAsText() {
    return this.logs.map(log => 
      `[${log.timestamp}] [${log.type.toUpperCase()}] ${log.message}`
    ).join('\n');
  }

  clear() {
    this.logs = [];
  }
}

export const consoleCapture = new ConsoleCapture(100);
